import qc from 'qc'
import _ from 'lodash'
import { ClipboardType, emptyClipboard, setClipboard } from './index'
import {
  isAudio,
  isDragonBoneChild,
  isEmptyNode,
  isScene,
  isVideo,
} from '@ps/bridge/qc/base'
import {
  executeAction,
  getAddAnimationAction,
  getRewriteAnimationAction,
} from '@ps/workbench/undo-redo'
import * as animationScript from '../../bridge/qc/animation'

/**
 * 判断节点是否有动画用于复制粘贴
 * @param node
 */
export function animationCanBeCopiedAndPasted(node: qc.Node): boolean {
  return (
    !isAudio(node) &&
    !isScene(node) &&
    !isDragonBoneChild(node) &&
    !isVideo(node) &&
    !isEmptyNode(node)
  )
}

/**
 * 目标节点与原始节点都支持动画，才可以粘贴
 */
export function animationCanBePasted(
  targetNodes: qc.Node[],
  originalNode: qc.Node,
): boolean {
  return (
    targetNodes.every((node) => animationCanBeCopiedAndPasted(node)) &&
    animationCanBeCopiedAndPasted(originalNode)
  )
}

type GetAnimationParams = {
  // 动画组 id（预设动画才是组），如果没有 id 则代表获取所有动画
  groupId?: string
  // 节点
  node: qc.Node
  // 动画 id
  uuid?: string
}

/**
 * 它接受一个节点、一个 groupId 和一个 uuid，并返回一个 Behaviours 数组。
 * @param {GetAnimationParams}  - `node` - 从中获取动画脚本的节点。
 */
export function getAnimationScripts({
  node,
  groupId,
  uuid,
}: GetAnimationParams): qc.Behaviour[] | void {
  if (animationCanBeCopiedAndPasted(node)) {
    // @ts-ignore
    const scripts = node.scripts.filter((script) => script.addType === 1) || []
    if (groupId) {
      // @ts-ignore
      // builtinId 一样为一组
      return scripts.filter((script) => script.builtinId === groupId)
    } else if (uuid) {
      return scripts.filter((script) => script.uuid === uuid)
    }
    return scripts
  }
}

type CopyAnimationParams = {
  // 动画组 id
  groupId: string
  // 节点
  node: qc.Node
  // 如果有传 animation，则使用该 animation，不需要从节点中获取
  animation?: qc.Behaviour[]
  uuid: string
}

export function copyAnimation({
  groupId,
  node,
  animation,
  uuid,
}: CopyAnimationParams): void {
  emptyClipboard()
  const clipboard: ClipboardType = {}
  clipboard.originalNode = node
  clipboard.animation =
    animation || getAnimationScripts({ node, groupId, uuid })
  setClipboard(clipboard)
}

type PasteAnimationParams = {
  // 目标节点
  targetNodes: qc.Node[]
  // 动画配置
  animations: any[]
  record?: boolean
  // 当前节点，复制完成后，需要返回当前节点添加的动画脚本
  currentNode: qc.Node
}

/**
 * 复制动画给指定节点
 * @param targetNodes
 * @param scripts 动画脚本配置
 */
export function pasteAnimation({
  targetNodes,
  animations,
  record = true,
  currentNode,
}: PasteAnimationParams): qc.Behaviour[] | undefined {
  if (!targetNodes || !currentNode || !animations) {
    throw new Error('targetNodes、currentNode、animations 为必传参数')
  }

  // 当前节点动画脚本列表
  let activeNodeAnimationScripts: qc.Behaviour[] = []
  // 节点动画脚本列表
  const animationScripts = animationScript.getAnimationScripts({
    targetNodes,
    animations,
  })

  if (animationScripts && !_.isEmpty(animationScripts)) {
    activeNodeAnimationScripts = animationScripts[
      currentNode.uuid
    ] as qc.Behaviour[]
    executeAction({
      action: getAddAnimationAction({
        newValue: {
          nodes: targetNodes,
          animationScripts: animationScripts,
        },
        oldValue: {
          nodes: targetNodes,
          animationScripts: animationScripts,
        },
      }),
      record,
    })
  }

  return activeNodeAnimationScripts
}

export function getNodesAnimationList(nodes: qc.Node[]) {
  const map: any = {}
  nodes.forEach((node) => {
    map[node.uuid] = getAnimationScripts({ node })
  })
  return map
}

/**
 * 覆盖节点动画
 * @param param0
 */
export function rewriteAnimation({
  targetNodes,
  animations,
  record = true,
  currentNode,
}: PasteAnimationParams): qc.Behaviour[] | undefined {
  if (!targetNodes || !currentNode || !animations) {
    throw new Error('targetNodes、currentNode、animations 为必传参数')
  }

  // 当前节点动画脚本列表
  let activeNodeAnimationScripts: qc.Behaviour[] = []

  // 节点动画脚本列表
  const animationScripts = animationScript.getAnimationScripts({
    targetNodes,
    animations,
  })
  const nodeAnimationScripts = getNodesAnimationList(targetNodes)

  if (animationScripts && !_.isEmpty(animationScripts)) {
    activeNodeAnimationScripts = animationScripts[
      currentNode.uuid
    ] as qc.Behaviour[]
    executeAction({
      action: getRewriteAnimationAction({
        newValue: {
          nodes: targetNodes,
          animationScripts: animationScripts,
          nodeAnimationScripts,
        },
        oldValue: {
          nodes: targetNodes,
          animationScripts: animationScripts,
          nodeAnimationScripts,
        },
      }),
      record,
    })
  }

  return activeNodeAnimationScripts
}
